/**
 * useContentFlags Hook
 *
 * Custom hook for fetching and managing content flags for moderation.
 * Provides real-time updates and filtering by status.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { ContentFlag, FlagStatus, FlagAction, GymRole, ModerationAction } from '@flash/shared';

/**
 * Filter options for content flags
 */
interface UseContentFlagsOptions {
  /** Gym ID to fetch flags for */
  gymId: string;
  /** Filter by status (optional, defaults to all) */
  status?: FlagStatus | 'all';
  /** Moderator's role (for audit logging) - if provided, audit logs will be written */
  moderatorRole?: GymRole | undefined;
}

/**
 * Return type for useContentFlags hook
 */
interface UseContentFlagsResult {
  /** List of content flags */
  flags: ContentFlag[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
  /** Counts by status */
  statusCounts: {
    pending: number;
    reviewed: number;
    actioned: number;
    dismissed: number;
    total: number;
  };
  /** Resolve a flag (take action on content) */
  resolveFlag: (flagId: string, action: FlagAction, notes?: string) => Promise<void>;
  /** Dismiss a flag (no action needed) */
  dismissFlag: (flagId: string, notes?: string) => Promise<void>;
  /** Mark flag as reviewed */
  markReviewed: (flagId: string, notes?: string) => Promise<void>;
  /** Refetch the flags */
  refetch: () => void;
}

/**
 * Hook to fetch and manage content flags for a gym
 *
 * @param options - Filter options including gymId and optional status filter
 * @returns Object containing flags, loading state, counts, and action functions
 *
 * @example
 * ```tsx
 * const { flags, loading, statusCounts, resolveFlag } = useContentFlags({
 *   gymId: 'apex-climbing',
 *   status: 'pending',
 * });
 *
 * // Resolve a flag
 * await resolveFlag(flagId, 'approved', 'Content removed');
 * ```
 */
/**
 * Write a moderation audit log entry
 */
const writeAuditLog = async (
  gymId: string,
  action: ModerationAction,
  moderatorId: string,
  moderatorDisplayName: string,
  moderatorRole: GymRole,
  targetId: string,
  details?: { reason?: string; relatedFlagId?: string }
): Promise<void> => {
  const db = getFirebaseDb();
  const auditRef = collection(db, 'gyms', gymId, 'auditLog');

  await addDoc(auditRef, {
    gymId,
    action,
    moderatorId,
    moderatorDisplayName,
    moderatorRole,
    targetType: 'flag',
    targetId,
    details,
    createdAt: serverTimestamp(),
  });
};

export const useContentFlags = (options: UseContentFlagsOptions): UseContentFlagsResult => {
  const { gymId, status = 'all', moderatorRole } = options;
  const { user } = useAuth();

  const [allFlags, setAllFlags] = useState<ContentFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Real-time subscription to flags
  useEffect(() => {
    if (!gymId) {
      setAllFlags([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const db = getFirebaseDb();
    const flagsRef = collection(db, 'flags');

    // Query all flags for this gym, ordered by creation date
    const flagsQuery = query(flagsRef, where('gymId', '==', gymId), orderBy('createdAt', 'desc'));

    const unsubscribe: Unsubscribe = onSnapshot(
      flagsQuery,
      (snapshot) => {
        const flagsData: ContentFlag[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: data['id'] as string,
            contentType: data['contentType'],
            contentId: data['contentId'] as string,
            gymId: data['gymId'] as string,
            reporterId: data['reporterId'] as string,
            reporterDisplayName: data['reporterDisplayName'] as string,
            reason: data['reason'],
            status: data['status'],
            createdAt: data['createdAt'],
            updatedAt: data['updatedAt'],
            // Optional fields
            ...(data['details'] ? { details: data['details'] as string } : {}),
            ...(data['resolution'] ? { resolution: data['resolution'] } : {}),
            ...(data['reviewedBy'] ? { reviewedBy: data['reviewedBy'] as string } : {}),
            ...(data['reviewedAt'] ? { reviewedAt: data['reviewedAt'] } : {}),
            ...(data['reviewNotes'] ? { reviewNotes: data['reviewNotes'] as string } : {}),
          } as ContentFlag;
        });

        setAllFlags(flagsData);
        setLoading(false);
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch flags';
        console.error('Error in flags subscription:', err);
        setError(errorMessage);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [gymId, refreshKey]);

  // Filter flags by status
  const flags = useMemo(() => {
    if (status === 'all') {
      return allFlags;
    }
    return allFlags.filter((flag) => flag.status === status);
  }, [allFlags, status]);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    return {
      pending: allFlags.filter((f) => f.status === 'pending').length,
      reviewed: allFlags.filter((f) => f.status === 'reviewed').length,
      actioned: allFlags.filter((f) => f.status === 'actioned').length,
      dismissed: allFlags.filter((f) => f.status === 'dismissed').length,
      total: allFlags.length,
    };
  }, [allFlags]);

  // Resolve a flag (take action on content)
  const resolveFlag = useCallback(
    async (flagId: string, action: FlagAction, notes?: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be authenticated to resolve flags');
      }

      const db = getFirebaseDb();
      const flagRef = doc(db, 'flags', flagId);

      // Build resolution object for Firestore (uses FieldValue for serverTimestamp)
      const resolution: Record<string, unknown> = {
        action,
        moderatorId: user.uid,
        moderatorDisplayName: user.displayName ?? 'Unknown',
        resolvedAt: serverTimestamp(),
      };

      if (notes) {
        resolution['note'] = notes;
      }

      await updateDoc(flagRef, {
        status: action === 'none' ? 'reviewed' : 'actioned',
        resolution,
      });

      // Write audit log entry
      if (moderatorRole) {
        const details: { reason?: string; relatedFlagId?: string } = { relatedFlagId: flagId };
        if (notes) {
          details.reason = notes;
        }
        await writeAuditLog(
          gymId,
          'flag_reviewed',
          user.uid,
          user.displayName ?? 'Unknown',
          moderatorRole,
          flagId,
          details
        );
      }
    },
    [user, gymId, moderatorRole]
  );

  // Dismiss a flag (no action needed)
  const dismissFlag = useCallback(
    async (flagId: string, notes?: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be authenticated to dismiss flags');
      }

      const db = getFirebaseDb();
      const flagRef = doc(db, 'flags', flagId);

      // Build resolution object for Firestore (uses FieldValue for serverTimestamp)
      const resolution: Record<string, unknown> = {
        action: 'none',
        moderatorId: user.uid,
        moderatorDisplayName: user.displayName ?? 'Unknown',
        resolvedAt: serverTimestamp(),
      };

      if (notes) {
        resolution['note'] = notes;
      }

      await updateDoc(flagRef, {
        status: 'dismissed',
        resolution,
      });

      // Write audit log entry
      if (moderatorRole) {
        const details: { reason?: string; relatedFlagId?: string } = { relatedFlagId: flagId };
        if (notes) {
          details.reason = notes;
        }
        await writeAuditLog(
          gymId,
          'flag_dismissed',
          user.uid,
          user.displayName ?? 'Unknown',
          moderatorRole,
          flagId,
          details
        );
      }
    },
    [user, gymId, moderatorRole]
  );

  // Mark flag as reviewed (for flags that need no action but were reviewed)
  const markReviewed = useCallback(
    async (flagId: string, notes?: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be authenticated to update flags');
      }

      const db = getFirebaseDb();
      const flagRef = doc(db, 'flags', flagId);

      // Build resolution object for Firestore (uses FieldValue for serverTimestamp)
      const resolution: Record<string, unknown> = {
        action: 'none',
        moderatorId: user.uid,
        moderatorDisplayName: user.displayName ?? 'Unknown',
        resolvedAt: serverTimestamp(),
      };

      if (notes) {
        resolution['note'] = notes;
      }

      await updateDoc(flagRef, {
        status: 'reviewed',
        resolution,
      });

      // Write audit log entry
      if (moderatorRole) {
        const details: { reason?: string; relatedFlagId?: string } = { relatedFlagId: flagId };
        if (notes) {
          details.reason = notes;
        }
        await writeAuditLog(
          gymId,
          'flag_reviewed',
          user.uid,
          user.displayName ?? 'Unknown',
          moderatorRole,
          flagId,
          details
        );
      }
    },
    [user, gymId, moderatorRole]
  );

  // Refetch function
  const refetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return {
    flags,
    loading,
    error,
    statusCounts,
    resolveFlag,
    dismissFlag,
    markReviewed,
    refetch,
  };
};

export default useContentFlags;
